import { Room, Player } from "@/hooks/useRoom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { QRCodeSVG } from "qrcode.react";
import { Copy, Crown, Sparkles, Users, Shuffle, X, Pencil, Check } from "lucide-react";
import { toast } from "sonner";
import { getRandomPrompt, PROMPTS, PromptCategory } from "@/lib/prompts";
import { useState } from "react";
import { Input } from "@/components/ui/input";
import { setStoredName } from "@/lib/client";
import RoomHeader from "@/components/RoomHeader";

interface Props {
  room: Room;
  players: Player[];
  amHost: boolean;
  me: Player;
}

const TEAM_LABEL: Record<string, { name: string; emoji: string; cls: string }> = {
  purple: { name: "Team Purple", emoji: "🟣", cls: "bg-team-purple text-white" },
  green: { name: "Team Green", emoji: "🟢", cls: "bg-team-green text-white" },
};

export default function Lobby({ room, players, amHost, me }: Props) {
  const [category, setCategory] = useState<PromptCategory>(
    (room.settings.category as PromptCategory) ?? "random",
  );
  const [rounds, setRounds] = useState<number>(room.settings.rounds ?? 5);
  const [timer, setTimer] = useState<number>(room.settings.timer ?? 30);

  const url = `${window.location.origin}/room/${room.code}`;

  const copyLink = () => {
    navigator.clipboard.writeText(url);
    toast.success("Link copied!");
  };

  const rebalance = async () => {
    const shuffled = [...players].sort(() => Math.random() - 0.5);
    await Promise.all(
      shuffled.map((p, i) =>
        supabase
          .from("players")
          .update({ team: i % 2 === 0 ? "purple" : "green" })
          .eq("id", p.id),
      ),
    );
    toast.success("Teams rebalanced");
  };

  const kick = async (id: string) => {
    await supabase.from("players").delete().eq("id", id);
  };

  const promote = async (id: string) => {
    await supabase.from("players").update({ is_host: true }).eq("id", id);
    await supabase.from("players").update({ is_host: false }).eq("id", me.id);
    await supabase.from("rooms").update({ host_id: id }).eq("id", room.id);
    toast.success("Host transferred");
  };

  const rename = async (id: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    await supabase.from("players").update({ name: trimmed }).eq("id", id);
    if (id === me.id) setStoredName(trimmed);
  };

  const startGame = async () => {
    if (players.length < 2) {
      toast.error("Need at least 2 players");
      return;
    }
    if (room.mode === "game") {
      // ensure everyone has a team
      const need = players.filter((p) => !p.team);
      if (need.length) {
        await Promise.all(
          need.map((p, i) =>
            supabase
              .from("players")
              .update({ team: i % 2 === 0 ? "purple" : "green" })
              .eq("id", p.id),
          ),
        );
      }
      const prompt = getRandomPrompt(category);
      const ends = new Date(Date.now() + timer * 1000).toISOString();
      const { data: rd } = await supabase
        .from("rounds")
        .insert({ room_id: room.id, round_number: 1, prompt, status: "collecting", ends_at: ends })
        .select()
        .single();
      await supabase
        .from("rooms")
        .update({
          status: "playing",
          current_round_id: rd!.id,
          settings: { rounds, timer, category },
        })
        .eq("id", room.id);
    } else if (room.mode === "poker") {
      // Poker: host enters story title in poker round screen; start with empty placeholder
      const { data: rd } = await supabase
        .from("rounds")
        .insert({ room_id: room.id, round_number: 1, prompt: "Story #1", status: "collecting" })
        .select()
        .single();
      await supabase
        .from("rooms")
        .update({ status: "playing", current_round_id: rd!.id })
        .eq("id", room.id);
    } else {
      // Retro: just flip status; the board itself creates the round.
      await supabase.from("rooms").update({ status: "playing" }).eq("id", room.id);
    }
  };

  return (
    <main className="min-h-screen px-4 pb-8">
      <RoomHeader room={room} me={me} amHost={amHost} players={players} />
      <div className="max-w-2xl mx-auto space-y-6 animate-pop-in pt-4">
        <header className="text-center space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">
            {room.mode === "game" ? "SameBrain Game" : room.mode === "poker" ? "Scrum Poker" : "Retro"}
          </div>
          <h1 className="text-5xl font-extrabold tracking-[0.3em]">{room.code}</h1>
        </header>

        <Card className="p-5 space-y-4 border-2 shadow-card">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white p-2 shadow-soft">
              <QRCodeSVG value={url} size={96} />
            </div>
            <div className="flex-1 space-y-2 min-w-0">
              <div className="text-xs font-semibold text-muted-foreground">SHARE THIS LINK</div>
              <div className="text-sm font-mono truncate">{url}</div>
              <Button onClick={copyLink} size="sm" variant="secondary" className="gap-2">
                <Copy className="h-4 w-4" /> Copy link
              </Button>
            </div>
          </div>
        </Card>

        <Card className="p-5 space-y-3 border-2 shadow-card">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 font-bold">
              <Users className="h-5 w-5 text-primary" />
              Players ({players.length})
            </div>
            {amHost && room.mode === "game" && (
              <Button size="sm" variant="outline" onClick={rebalance} className="gap-1.5">
                <Shuffle className="h-3.5 w-3.5" /> Rebalance
              </Button>
            )}
          </div>
          <div className="space-y-2">
            {players.map((p) => (
              <PlayerRow
                key={p.id}
                player={p}
                isMe={p.client_id === me.client_id}
                amHost={amHost}
                team={p.team ? TEAM_LABEL[p.team] : null}
                onKick={() => kick(p.id)}
                onPromote={() => promote(p.id)}
                onRename={(name) => rename(p.id, name)}
              />
            ))}
          </div>
        </Card>

        {amHost && room.mode === "game" && (
          <Card className="p-5 space-y-4 border-2 shadow-card">
            <div className="font-bold flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-secondary" /> Game settings
            </div>
            <div className="grid grid-cols-2 gap-3">
              <label className="space-y-1">
                <div className="text-xs font-semibold text-muted-foreground">Rounds</div>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={rounds}
                  onChange={(e) => setRounds(Number(e.target.value))}
                >
                  {[3, 5, 7].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
              <label className="space-y-1">
                <div className="text-xs font-semibold text-muted-foreground">Timer (sec)</div>
                <select
                  className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm"
                  value={timer}
                  onChange={(e) => setTimer(Number(e.target.value))}
                >
                  {[15, 30, 45, 60].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </label>
            </div>
            <label className="space-y-1 block">
              <div className="text-xs font-semibold text-muted-foreground">Prompt category</div>
              <div className="grid grid-cols-3 gap-2">
                {(Object.keys(PROMPTS) as PromptCategory[]).map((c) => (
                  <button
                    key={c}
                    onClick={() => setCategory(c)}
                    className={`rounded-lg border-2 p-2 text-sm font-semibold capitalize transition-bounce ${
                      category === c ? "border-primary bg-primary/10" : "border-border hover:border-primary/50"
                    }`}
                  >
                    {c}
                  </button>
                ))}
              </div>
            </label>
          </Card>
        )}

        {amHost ? (
          <Button
            onClick={startGame}
            size="lg"
            className="w-full h-14 text-base font-bold gradient-fun shadow-pop"
            disabled={players.length < 2}
          >
            {players.length < 2 ? "Waiting for players…" : "Start"}
          </Button>
        ) : (
          <div className="text-center text-muted-foreground animate-pulse">
            Waiting for the host to start…
          </div>
        )}
      </div>
    </main>
  );
}

interface PlayerRowProps {
  player: Player;
  isMe: boolean;
  amHost: boolean;
  team: { name: string; emoji: string; cls: string } | null;
  onKick: () => void;
  onPromote: () => void;
  onRename: (name: string) => void;
}

function PlayerRow({ player, isMe, amHost, team, onKick, onPromote, onRename }: PlayerRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(player.name);
  const canRename = isMe || amHost;

  const save = () => {
    if (draft.trim() && draft.trim() !== player.name) onRename(draft.trim());
    setEditing(false);
  };

  return (
    <div className="flex items-center gap-3 rounded-xl border bg-card p-3 animate-pop-in">
      <div className="h-9 w-9 rounded-full gradient-primary flex items-center justify-center text-white font-bold">
        {player.name[0]?.toUpperCase()}
      </div>
      {editing ? (
        <div className="flex-1 flex gap-2">
          <Input
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={20}
            className="h-9"
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
              if (e.key === "Escape") {
                setDraft(player.name);
                setEditing(false);
              }
            }}
          />
          <Button size="sm" onClick={save} className="h-9 px-2">
            <Check className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="flex-1 font-semibold flex items-center gap-2 min-w-0">
          <span className="truncate">{player.name}</span>
          {player.is_host && <Crown className="h-4 w-4 text-warning shrink-0" />}
          {isMe && <span className="text-xs text-muted-foreground">(you)</span>}
          {canRename && (
            <button
              onClick={() => {
                setDraft(player.name);
                setEditing(true);
              }}
              className="text-muted-foreground hover:text-primary p-0.5"
              aria-label="Rename"
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}
      {team && !editing && (
        <span className={`text-xs font-bold px-2.5 py-1 rounded-full ${team.cls}`}>
          {team.emoji} {team.name.replace("Team ", "")}
        </span>
      )}
      {amHost && !player.is_host && !editing && (
        <>
          <button
            onClick={onPromote}
            className="text-muted-foreground hover:text-warning p-1"
            aria-label="Promote to host"
            title="Make host"
          >
            <Crown className="h-4 w-4" />
          </button>
          <button
            onClick={onKick}
            className="text-muted-foreground hover:text-destructive p-1"
            aria-label="Kick player"
          >
            <X className="h-4 w-4" />
          </button>
        </>
      )}
    </div>
  );
}
